import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { Mango, Fisherwoman, Coconut, Hibiscus } from "@/components/KonkanVectors";

const V = ({ children, className }) => (
  <div className={`absolute pointer-events-none select-none ${className}`} aria-hidden="true">{children}</div>
);

const getBadge = (h, idx) => {
  if (h.average_rating >= 4.8 && (h.review_count || 0) >= 3)
    return { label: "Most Loved", cls: "bg-[#D88A2B] text-white" };
  if (h.average_rating >= 4.5)
    return { label: "Top Rated", cls: "bg-white/90 backdrop-blur-sm text-[#2F5D3A]" };
  if ((h.view_count || 0) >= 5)
    return { label: "Popular", cls: "bg-[#2B8C9F]/90 text-white" };
  if (h.is_featured)
    return { label: "Guest Favorite", cls: "bg-[#C86A3A]/90 text-white" };
  if (idx < 3)
    return { label: "Recommended", cls: "bg-white/90 backdrop-blur-sm text-[#2F5D3A]" };
  return null;
};

export const FeaturedHomestays = ({ homestays }) => {
  if (homestays.length === 0) return null;

  return (
    <section className="relative py-12 md:py-16 px-6 md:px-8 bg-white overflow-hidden" data-testid="featured-homestays-section">
      <V className="right-2 md:right-8 top-4 w-20 md:w-32 opacity-[0.20]"><Mango /></V>
      <V className="-left-2 md:left-4 bottom-0 w-24 md:w-36 opacity-[0.18]"><Fisherwoman /></V>
      <V className="left-1/3 top-2 w-12 md:w-16 opacity-[0.15]"><Coconut /></V>
      <V className="right-1/4 bottom-6 w-16 md:w-24 opacity-[0.16]"><Hibiscus /></V>
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">Featured Homestays</h2>
          <Link to="/listings" className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary hover:underline">
            View all homestays <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 snap-x snap-mandatory scrollbar-thin" style={{ scrollbarWidth: 'thin' }}>
          {homestays.map((h, idx) => {
            const badge = getBadge(h, idx);
            return (
              <Link
                key={h.id}
                to={`/homestay/${h.slug || h.id}`}
                className="flex-shrink-0 w-[260px] md:w-[280px] group"
                data-testid={`homestay-card-${h.id}`}
              >
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-2.5">
                  <img src={h.images[0]} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  {badge && (
                    <span className={`absolute top-2.5 left-2.5 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm ${badge.cls}`}>
                      {badge.label}
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">{h.name}</h3>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {h.location_name || h.sub_location_name || "Konkan"}
                </p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-sm font-bold text-foreground">
                    {h.price_per_night ? `₹${Math.round(h.price_per_night).toLocaleString()}` : "Price on request"}
                    {h.price_per_night && <span className="text-xs font-normal text-muted-foreground"> /night</span>}
                  </span>
                  {h.average_rating > 0 && (
                    <span className="flex items-center gap-1 text-xs font-medium">
                      <Star className="w-3.5 h-3.5 fill-[#D88A2B] text-[#D88A2B]" />
                      {h.average_rating.toFixed(1)}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
        <Link to="/listings" className="sm:hidden flex items-center justify-center gap-1 text-sm font-medium text-primary hover:underline mt-4">
          View all homestays <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};
